import { useState, useEffect, useCallback } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Alert,
  AlertIcon,
  HStack,
  Badge,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Link,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import { CopyIcon, SearchIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import PageLoader from "./components/PageLoader";
import api from "./api";

// ── helpers ──────────────────────────────────────────────────────────────────

function shortAddr(addr: string | undefined) {
  if (!addr) return "—";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function fmtDate(val: string | number | undefined) {
  if (!val) return "—";
  return new Date(val).toLocaleString();
}

function fmtUsd(val: string | number | undefined) {
  const n = typeof val === "string" ? parseFloat(val) : (val ?? 0);
  if (!n || isNaN(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function CopyBtn({ text }: { text: string }) {
  return (
    <CopyIcon
      cursor="pointer"
      ml={1}
      onClick={() => {
        navigator.clipboard.writeText(text);
        toast.success("Copied!");
      }}
    />
  );
}

// ── Agents tab ────────────────────────────────────────────────────────────────

function AgentsTab() {
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [syncingAddress, setSyncingAddress] = useState<string | null>(null);

  const fetchAgents = useCallback(
    async (p = 1, q = search) => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ page: String(p), limit: "50" });
        if (q) params.set("search", q);
        const res = await api.get(`/api/hyperliquid/agents?${params}`);
        const data = res.data?.data;
        const list: any[] = data?.agents ?? [];
        setAgents(list);
        setTotal(data?.pagination?.total ?? 0);
        setTotalPages(data?.pagination?.pages ?? 1);
        setActiveCount(list.filter((a) => a.isActive).length);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Failed to load agents");
      } finally {
        setLoading(false);
      }
    },
    [search]
  );

  useEffect(() => {
    fetchAgents(1, "");
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchAgents(1, search);
  };

  const handleSync = async (address: string) => {
    setSyncingAddress(address);
    try {
      const res = await api.post(`/api/admin/trades/sync/${address}`);
      const data = res.data?.data;
      toast.success(`Synced ${data?.inserted ?? 0} new fills for ${address.slice(0, 6)}…${address.slice(-4)}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Sync failed");
    } finally {
      setSyncingAddress(null);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <StatCard label="Total Agents" value={total} color="blue.400" />
        <StatCard label="Active Agents" value={activeCount} color="green.400" />
        <StatCard label="Inactive Agents" value={total - activeCount} color="red.400" />
      </SimpleGrid>

      <HStack spacing={3}>
        <InputGroup maxW="350px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </InputGroup>
        <Button size="sm" colorScheme="blue" onClick={handleSearch}>
          Search
        </Button>
        {search && (
          <Button
            size="sm"
            onClick={() => {
              setSearch("");
              setPage(1);
              fetchAgents(1, "");
            }}
          >
            Clear
          </Button>
        )}
      </HStack>

      {loading && <PageLoader message="Loading HL agents…" />}
      {error && (
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>User Address</Th>
                <Th>Agent Wallet</Th>
                <Th>Custody Wallet</Th>
                <Th>Status</Th>
                <Th>Expires</Th>
                <Th>Created</Th>
                <Th>Fills</Th>
              </Tr>
            </Thead>
            <Tbody>
              {agents.map((agent, idx) => (
                <Tr key={agent._id ?? idx}>
                  <Td>{(page - 1) * 50 + idx + 1}</Td>
                  <Td>
                    <HStack spacing={1}>
                      <Text fontFamily="mono" fontSize="sm">
                        {shortAddr(agent.userAddress)}
                      </Text>
                      <CopyBtn text={agent.userAddress ?? ""} />
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Text fontFamily="mono" fontSize="sm">
                        {shortAddr(agent.agentWalletAddress)}
                      </Text>
                      <CopyBtn text={agent.agentWalletAddress ?? ""} />
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Text fontFamily="mono" fontSize="sm">
                        {shortAddr(agent.custodyWalletAddress)}
                      </Text>
                      <CopyBtn text={agent.custodyWalletAddress ?? ""} />
                    </HStack>
                  </Td>
                  <Td>
                    <Badge colorScheme={agent.isActive ? "green" : "red"}>
                      {agent.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </Td>
                  <Td fontSize="sm">{fmtDate(agent.expiresAt)}</Td>
                  <Td fontSize="sm">{fmtDate(agent.createdAt)}</Td>
                  <Td>
                    <Tooltip label="Re-sync HL fills for this user">
                      <Button
                        size="xs"
                        colorScheme="purple"
                        variant="outline"
                        isDisabled={syncingAddress !== null}
                        onClick={() => handleSync(agent.userAddress)}
                      >
                        {syncingAddress === agent.userAddress ? <Spinner size="xs" /> : "Sync"}
                      </Button>
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
              {agents.length === 0 && (
                <Tr>
                  <Td colSpan={8} textAlign="center" color="gray.400">
                    No agents found
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {totalPages > 1 && (
        <HStack justify="center" spacing={2}>
          <Button size="sm" isDisabled={page <= 1} onClick={() => { setPage(page - 1); fetchAgents(page - 1); }}>
            ← Prev
          </Button>
          <Text fontSize="sm" color="gray.400">
            Page {page} / {totalPages}
          </Text>
          <Button size="sm" isDisabled={page >= totalPages} onClick={() => { setPage(page + 1); fetchAgents(page + 1); }}>
            Next →
          </Button>
        </HStack>
      )}
    </VStack>
  );
}

// ── HL Fills tab ──────────────────────────────────────────────────────────────

function FillsTab() {
  const [fills, setFills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchFills = useCallback(
    async (p = 1, q = search) => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ page: String(p), limit: "20" });
        if (q) params.set("search", q);
        const res = await api.get(`/api/admin/trades/hl-fills?${params}`);
        const data = res.data?.data;
        setFills(data?.fills ?? []);
        setTotal(data?.pagination?.total ?? 0);
        setTotalPages(data?.pagination?.pages ?? 1);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Failed to load fills");
      } finally {
        setLoading(false);
      }
    },
    [search]
  );

  useEffect(() => {
    fetchFills(1, "");
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchFills(1, search);
  };

  return (
    <VStack spacing={6} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <StatCard label="Total HL Fills" value={total} color="purple.400" />
        <StatCard label="Page" value={`${page} / ${totalPages}`} color="orange.400" />
      </SimpleGrid>

      <HStack spacing={3}>
        <InputGroup maxW="350px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search address, symbol, hash…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </InputGroup>
        <Button size="sm" colorScheme="blue" onClick={handleSearch}>
          Search
        </Button>
        {search && (
          <Button
            size="sm"
            onClick={() => {
              setSearch("");
              setPage(1);
              fetchFills(1, "");
            }}
          >
            Clear
          </Button>
        )}
      </HStack>

      {loading && <PageLoader message="Loading HL fills…" />}
      {error && (
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>User</Th>
                <Th>Symbol</Th>
                <Th>Side</Th>
                <Th>Size</Th>
                <Th>Price</Th>
                <Th>Notional</Th>
                <Th>PnL</Th>
                <Th>Fee</Th>
                <Th>Tx</Th>
                <Th>Time</Th>
              </Tr>
            </Thead>
            <Tbody>
              {fills.map((fill, idx) => {
                const pnl = parseFloat(fill.closedPnl ?? "0");
                return (
                  <Tr key={fill._id ?? idx}>
                    <Td>{(page - 1) * 20 + idx + 1}</Td>
                    <Td>
                      <HStack spacing={1}>
                        <Text fontFamily="mono" fontSize="xs">
                          {shortAddr(fill.userAddress)}
                        </Text>
                        <CopyBtn text={fill.userAddress ?? ""} />
                      </HStack>
                    </Td>
                    <Td fontWeight="semibold">{fill.symbol}</Td>
                    <Td>
                      <Badge colorScheme={fill.side === "B" || fill.side === "Buy" ? "green" : "red"}>
                        {fill.side === "B" ? "BUY" : fill.side === "A" ? "SELL" : fill.side}
                      </Badge>
                    </Td>
                    <Td>{fill.size}</Td>
                    <Td>{fmtUsd(fill.price)}</Td>
                    <Td>{fmtUsd(fill.notionalUsd)}</Td>
                    <Td color={pnl > 0 ? "green.400" : pnl < 0 ? "red.400" : "gray.400"}>
                      {pnl !== 0 ? fmtUsd(pnl) : "—"}
                    </Td>
                    <Td fontSize="xs">{fmtUsd(fill.fee)}</Td>
                    <Td>
                      {fill.hash ? (
                        <Link
                          href={`https://app.hyperliquid.xyz/explorer/tx/${fill.hash}`}
                          isExternal
                          color="blue.400"
                          fontSize="xs"
                        >
                          {shortAddr(fill.hash)} <ExternalLinkIcon mx="2px" />
                        </Link>
                      ) : (
                        "—"
                      )}
                    </Td>
                    <Td fontSize="xs">{fill.time ? new Date(fill.time).toLocaleString() : "—"}</Td>
                  </Tr>
                );
              })}
              {fills.length === 0 && (
                <Tr>
                  <Td colSpan={11} textAlign="center" color="gray.400">
                    No fills found
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {totalPages > 1 && (
        <HStack justify="center" spacing={2}>
          <Button size="sm" isDisabled={page <= 1} onClick={() => { setPage(page - 1); fetchFills(page - 1); }}>
            ← Prev
          </Button>
          <Text fontSize="sm" color="gray.400">
            Page {page} / {totalPages}
          </Text>
          <Button size="sm" isDisabled={page >= totalPages} onClick={() => { setPage(page + 1); fetchFills(page + 1); }}>
            Next →
          </Button>
        </HStack>
      )}
    </VStack>
  );
}

// ── Shared stat card ──────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <Box
      p={5}
      bg="gray.800"
      borderRadius="lg"
      borderWidth="1px"
      borderColor="gray.700"
      borderLeftWidth="4px"
      borderLeftColor={color}
    >
      <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">
        {label}
      </Text>
      <Text fontSize="2xl" fontWeight="bold" color="white" mt={1}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </Text>
    </Box>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function Hyperliquid() {
  return (
    <Container maxW="container.xl" py={8}>
      <Toaster position="top-right" />
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="xl" textAlign="center">
          Hyperliquid
        </Heading>
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab>Agents</Tab>
            <Tab>Fills</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <AgentsTab />
            </TabPanel>
            <TabPanel px={0}>
              <FillsTab />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
}

export default Hyperliquid;
